import { useCarbucksApi } from './Api/CarbucksApi';
import { type ICarbucksViewModel, useViewModelInstance as useCarbucksViewModelInstance} from "@app/Carbucks";
import { Field, FormModel } from '@library/Components/Forms/Form';
import { useConfirm, ModalButtons } from "@library/Components/Modal/ModalTypes";
import { nextTick, ref } from 'vue';
import { useRouter } from "@library/Services";
import { reactive } from 'vue';
import type { MaskaDetail } from 'maska';
import type { IBankAccount } from '@domain/Carbucks';

export async function useManageBankAccountViewModel()
{
    const viewModel = new ManageBankAccountViewModel();
    await viewModel.initialise();
    return viewModel;
}

export enum ManageBankAccountFormState 
{
    AllowInput = 0,
    Readonly = 1
}

export class ManageBankAccountViewModel
{
    carbucks!: ICarbucksViewModel;// await useCarbucksViewModelInstance();

    formModel = ref<IBankAccount>({ bsb: undefined, accountNumber: undefined });

    masks = {
        [ManageBankAccountFormState.AllowInput]: {
            bsb: '###-###',
            accountNumber: '####-####-##'
        },
        [ManageBankAccountFormState.Readonly]: {
            bsb: '!*!*!*-###',
            accountNumber: '!*!*!*!*-!*!*#-##',
        }
    };

    formState = ref<ManageBankAccountFormState>( ManageBankAccountFormState.Readonly );
    errorState = ref(false);

    maskaOptions = {
        bsb: reactive({
            mask: '',
            unmasked: '',
            onMaska: (detail: MaskaDetail) => 
            {
                this.maskaOptions.bsb.unmasked = detail.unmasked; 
            }
        }),
        accountNumber: reactive({
            mask: '',
            unmasked: '',
            onMaska: (detail: MaskaDetail) => 
            {
                this.maskaOptions.accountNumber.unmasked = detail.unmasked; 
            }
        })
    };

    validation = {
        bsb: [
            Field.required( `The BSB field is required` ), // Regex pattern/Maska takes care of min/max character counts
            Field.regexPattern( /^\d{3}-\d{3}$/, 'Must be a valid BSB' ),
        ],
        accountNumber: [
            Field.required( `The Account Number field is required` ), // Regex pattern/Maska takes care of min/max character counts
            Field.regexPattern( /^\d{4}-\d{2,4}($|-\d{1,2}$)/, 'Must be a valid Account Number' )
        ]
    };

    async initialise()
    {
        this.carbucks = await useCarbucksViewModelInstance();
        
        this.formModel.value = {
            bsb: this.carbucks.profile.bankAccount?.bsb ?? '',
            accountNumber: this.carbucks.profile.bankAccount?.accountNumber ?? ''
        };

        this.formState.value = this.carbucks.profile.bankAccount?.bsb && this.carbucks.profile.bankAccount?.accountNumber ? ManageBankAccountFormState.Readonly : ManageBankAccountFormState.AllowInput;

        this.maskaOptions.bsb.mask = this.masks[this.formState.value].bsb;
        this.maskaOptions.bsb.unmasked = this.carbucks.profile.bankAccount?.bsb ?? '';

        this.maskaOptions.accountNumber.mask = this.masks[this.formState.value].accountNumber;
        this.maskaOptions.accountNumber.unmasked = this.carbucks.profile.bankAccount?.accountNumber ?? '';
    }

    async upsertBankAccount()
    {
        this.resetErrorState();

        var api = useCarbucksApi();

        const response = await api.upsertBankAccount( this.maskaOptions.bsb.unmasked, this.maskaOptions.accountNumber.unmasked );

        if ( response.success )
        {   
            this.carbucks.profile.updateBankAccount( response.data.bsb, response.data.accountNumber );

            const router = useRouter();

            router.back();
        }
        else
        {
            this.errorState.value = true;
        }
    }

    async deleteBankAccount()
    {
        this.resetErrorState();

        const result = await useConfirm( 'Are you sure you want to remove this bank account?', '', [
            ModalButtons.Primary( 'Remove', true ),
            ModalButtons.Close( 'Cancel', false )
        ] );

        if ( result )
        {
            var api = useCarbucksApi();

            const response = await api.deleteBankAccount();

            if ( response.success && response.data )
            {
                this.carbucks.profile.updateBankAccount( undefined, undefined );

                const router = useRouter();

                router.back();
            }
            else
            {
                this.errorState.value = true;
            }
        }
    }

    async resetForm( form: FormModel )
    {
        this.resetErrorState();
        
        this.clearValues();
        
        this.applyFormState(ManageBankAccountFormState.AllowInput);
        
        nextTick( () => // Prevents the UI from displaying the fields as invalid before any user input (such as focus)
        {
            form.reset();
            form.resetValidation();
        });
    }

    resetErrorState()
    {
        this.errorState.value = false;
    }

    clearValues()
    {
        this.formModel.value.bsb = '';
        this.formModel.value.accountNumber = '';
    }

    applyFormState( newState: ManageBankAccountFormState )
    {
        this.formState.value = newState;
        this.maskaOptions.accountNumber.mask = this.masks[this.formState.value].accountNumber;
        this.maskaOptions.bsb.mask = this.masks[this.formState.value].bsb;
    }

}